// Skip Navigation Component
// Provides skip link for keyboard users to bypass repetitive navigation
// WCAG 2.1 AA Success Criterion 2.4.1 (Bypass Blocks)

import React from 'react'

export interface SkipLink {
  /** Target element ID (e.g., "main-content") */
  targetId: string
  /** Link text displayed to user */
  label: string
}

interface SkipNavigationProps {
  /** Array of skip links to render */
  links?: SkipLink[]
}

const defaultLinks: SkipLink[] = [
  { targetId: 'main-content', label: 'Pular para o conteúdo principal' },
  { targetId: 'main-navigation', label: 'Pular para a navegação' },
]

/**
 * Skip Navigation Component
 *
 * Renders visually hidden links that become visible on keyboard focus.
 * Allows keyboard users to bypass repetitive navigation and jump to main content.
 *
 * Usage:
 * 1. Add <SkipNavigation /> at the top of your root component
 * 2. Add id="main-content" to your main content container
 * 3. Add id="main-navigation" to your navigation container (optional)
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <>
 *       <SkipNavigation />
 *       <nav id="main-navigation">...</nav>
 *       <main id="main-content">...</main>
 *     </>
 *   )
 * }
 * ```
 */
export const SkipNavigation: React.FC<SkipNavigationProps> = ({
  links = defaultLinks,
}) => {
  const handleSkipClick = (event: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    event.preventDefault()

    const target = document.getElementById(targetId)
    if (target) {
      // Set tabindex to allow programmatic focus
      target.setAttribute('tabindex', '-1')
      target.focus()

      // Scroll target into view
      target.scrollIntoView({ behavior: 'smooth', block: 'start' })

      // Remove tabindex after focus (cleanup)
      target.addEventListener('blur', () => {
        target.removeAttribute('tabindex')
      }, { once: true })
    }
  }

  return (
    <div className="sr-only-focusable">
      {links.map((link) => (
        <a
          key={link.targetId}
          href={`#${link.targetId}`}
          className="skip-nav"
          onClick={(e) => handleSkipClick(e, link.targetId)}
        >
          {link.label}
        </a>
      ))}
    </div>
  )
}
